import React from "react";
import { formatCurrency, formatDate } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Skeleton } from "../ui/skeleton";
import { Button } from "../ui/button";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  AreaChart, Area,
} from "recharts";
import {
  DollarSign, FileText, CheckCircle2, CalendarClock, Building2, ArrowRight, Upload, Receipt,
  AlertTriangle, TrendingUp, Truck, Landmark, ChevronRight,
} from "lucide-react";

export const CHART_COLORS = ["hsl(199 89% 33%)", "hsl(160 60% 38%)", "hsl(35 92% 55%)", "hsl(222 47% 35%)", "hsl(0 72% 51%)"];

// Static chart config objects hoisted to module scope so they keep a stable
// reference across renders (avoids recreating them and re-rendering recharts).
const AREA_MARGIN = { top: 10, right: 10, left: 0, bottom: 0 };
const BAR_MARGIN = { top: 10, right: 10, left: 0, bottom: 28 };
const AXIS_TICK = { fontSize: 12, fill: "hsl(215 16% 40%)" };
const AXIS_TICK_SM = { fontSize: 11, fill: "hsl(215 16% 40%)" };
const CHART_CURSOR = { fill: "hsl(210 25% 96%)" };

const monthLabel = (m) => {
  const [y, mm] = m.split("-");
  const d = new Date(Number(y), Number(mm) - 1, 1);
  return d.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
};

const Kpi = ({ label, value, icon: Icon, sub, testid, tint }) => (
  <Card className="transition-[box-shadow,border-color] duration-200 hover:shadow-sm">
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</div>
          <div data-testid={testid} className="mt-2 text-2xl sm:text-3xl font-semibold font-heading tabular-nums">{value}</div>
          {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
        </div>
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tint}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export const DashboardLoading = () => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
    </div>
    <Skeleton className="h-80 rounded-xl" />
  </div>
);

export const DashboardEmpty = ({ onUpload }) => (
  <Card>
    <CardContent className="flex flex-col items-center justify-center py-20 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-accent mb-4">
        <Receipt className="h-8 w-8 text-primary" />
      </div>
      <h3 className="font-heading text-lg font-semibold">No documents yet</h3>
      <p className="mt-1 text-sm text-muted-foreground max-w-sm">
        Upload toll-bill PDFs (scanned or digital) to start extracting structured data and see analytics here.
      </p>
      <Button className="mt-5 gap-2" data-testid="empty-upload-cta" onClick={onUpload}>
        <Upload className="h-4 w-4" /> Upload your first PDF
      </Button>
    </CardContent>
  </Card>
);

export const NeedsReviewBanner = ({ count, onReview }) => (
  <button
    type="button"
    data-testid="dashboard-needs-review-banner"
    onClick={onReview}
    className="w-full flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-left transition-colors hover:bg-amber-100/70"
  >
    <span className="flex items-center gap-2 text-sm text-amber-800">
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span><strong>{count}</strong> bill{count > 1 ? "s" : ""} may need review (missing fields or inconsistent totals).</span>
    </span>
    <span className="flex items-center gap-1 text-sm font-medium text-amber-800 shrink-0">Review <ArrowRight className="h-4 w-4" /></span>
  </button>
);

export const KpiGrid = ({ data, needsReview, anomalies }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
    <Kpi testid="kpi-total-amount-due" label="Total amount due" value={formatCurrency(data.total_amount_due)} sub={`${data.total_bills} bills`} icon={DollarSign} tint="bg-sky-50 text-sky-700" />
    <Kpi testid="kpi-documents-processed" label="Documents processed" value={data.total_documents} sub={`${data.total_bills} bills extracted`} icon={FileText} tint="bg-slate-100 text-slate-700" />
    <Kpi testid="kpi-processing-success-rate" label="Success rate" value={`${data.success_rate}%`} sub={`${data.total_transactions} transactions`} icon={CheckCircle2} tint="bg-emerald-50 text-emerald-700" />
    <Kpi testid="kpi-needs-review" label="Needs review" value={needsReview} sub={anomalies.length ? `${anomalies.length} anomalies` : "all looks consistent"} icon={AlertTriangle} tint="bg-amber-50 text-amber-700" />
  </div>
);

export const MonthlySpendChart = ({ monthChart }) => (
  <Card className="lg:col-span-2">
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-semibold tracking-wide flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-muted-foreground" /> Monthly spend
      </CardTitle>
    </CardHeader>
    <CardContent>
      {monthChart.length === 0 ? (
        <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">Not enough dated bills to show a trend yet.</div>
      ) : (
        <div data-testid="chart-monthly-spend" className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <AreaChart data={monthChart} margin={AREA_MARGIN}>
              <defs>
                <linearGradient id="spendFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(199 89% 33%)" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="hsl(199 89% 33%)" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(214 20% 90%)" vertical={false} />
              <XAxis dataKey="month" tickFormatter={monthLabel} tick={AXIS_TICK} tickLine={false} axisLine={false} />
              <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} />
              <Tooltip formatter={(v) => formatCurrency(v)} labelFormatter={monthLabel} cursor={CHART_CURSOR} />
              <Area type="monotone" dataKey="amount" stroke="hsl(199 89% 33%)" strokeWidth={2} fill="url(#spendFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </CardContent>
  </Card>
);

export const TopPlates = ({ topPlates }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-semibold tracking-wide flex items-center gap-2">
        <Truck className="h-4 w-4 text-muted-foreground" /> Top plates by spend
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-2" data-testid="dashboard-top-plates">
      {topPlates.length === 0 ? (
        <div className="py-8 text-center text-sm text-muted-foreground">No plate data yet.</div>
      ) : (
        topPlates.slice(0, 6).map((p) => (
          <div key={p.license_plate} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
            <div className="min-w-0">
              <div className="text-sm font-mono font-medium truncate">{p.license_plate}</div>
              <div className="text-xs text-muted-foreground truncate">{p.unit_number ? `${p.unit_type || "Unit"} ${p.unit_number}` : `${p.count} bill(s)`}</div>
            </div>
            <div className="text-sm font-semibold tabular-nums">{formatCurrency(p.amount)}</div>
          </div>
        ))
      )}
    </CardContent>
  </Card>
);

export const AuthorityChart = ({ authChart }) => (
  <Card className="lg:col-span-2">
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-semibold tracking-wide flex items-center gap-2">
        <Building2 className="h-4 w-4 text-muted-foreground" /> Amount due by authority
      </CardTitle>
    </CardHeader>
    <CardContent>
      {authChart.length === 0 ? (
        <div className="h-80 flex items-center justify-center text-sm text-muted-foreground">Not enough data to show trends yet.</div>
      ) : (
        <div data-testid="chart-amount-by-authority" className="h-80 w-full">
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <BarChart data={authChart} margin={BAR_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(214 20% 90%)" vertical={false} />
              <XAxis
                dataKey="name"
                tick={AXIS_TICK_SM}
                tickLine={false}
                axisLine={false}
                interval={0}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} />
              <Tooltip formatter={(v) => formatCurrency(v)} labelFormatter={(_, p) => (p && p[0] && p[0].payload.full) || ""} cursor={CHART_CURSOR} />
              <Bar dataKey="amount" radius={[6, 6, 0, 0]}>
                {authChart.map((e, i) => <Cell key={e.full || i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </CardContent>
  </Card>
);

export const UpcomingDue = ({ upcoming }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-semibold tracking-wide flex items-center gap-2">
        <CalendarClock className="h-4 w-4 text-muted-foreground" /> Upcoming due dates
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-2">
      {upcoming.length === 0 ? (
        <div className="py-8 text-center text-sm text-muted-foreground">No upcoming due dates.</div>
      ) : (
        upcoming.slice(0, 6).map((u) => (
          <div key={u.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{(u.issuing_authority || "Unknown").replace(/\(.*\)/, "").trim()}</div>
              <div className="text-xs text-muted-foreground">{formatDate(u.due_date)} · {u.license_plate || "—"}</div>
            </div>
            <div className="text-sm font-semibold tabular-nums">{formatCurrency(u.total_amount_due)}</div>
          </div>
        ))
      )}
    </CardContent>
  </Card>
);

export const AnomaliesCard = ({ anomalies, onOpen }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-semibold tracking-wide flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-600" /> Anomalies detected ({anomalies.length})
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-2" data-testid="dashboard-anomalies">
      {anomalies.slice(0, 8).map((a, i) => (
        <button
          key={a.bill_id || `${a.document_id}-${a.type}` || i}
          type="button"
          onClick={() => onOpen(a.document_id)}
          className="w-full flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2 text-left transition-colors hover:bg-[hsl(210_25%_96%)]"
        >
          <div className="min-w-0">
            <div className="text-sm truncate">{a.message}</div>
            <div className="text-xs text-muted-foreground">{(a.issuing_authority || "Unknown")} · {a.license_plate || "—"}</div>
          </div>
          <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium ${a.severity === "warning" ? "border-amber-200 bg-amber-50 text-amber-800" : "border-slate-200 bg-slate-50 text-slate-600"}`}>
            {a.type.replace(/_/g, " ")}
          </span>
        </button>
      ))}
    </CardContent>
  </Card>
);

export const CreditCollectionsCard = ({ creditCollections, onOpen }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-semibold tracking-wide flex items-center gap-2">
        <Landmark className="h-4 w-4 text-rose-600" /> Credit collections &amp; Enforcements ({creditCollections.count})
        <span className="ml-auto text-sm font-semibold tabular-nums">{formatCurrency(creditCollections.amount)}</span>
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-2" data-testid="dashboard-credit-collections">
      <p className="text-xs text-muted-foreground -mt-1 mb-1">Bills in collections, enforcement or final notice (excluded from the authority chart). Click to open.</p>
      {(creditCollections.items && creditCollections.items.length > 0
        ? creditCollections.items
        : []
      ).map((c, i) => (
        <button
          key={c.bill_id || i}
          type="button"
          onClick={() => onOpen(c.document_id)}
          className="w-full flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2 text-left transition-colors hover:bg-[hsl(210_25%_96%)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="credit-collection-row"
        >
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{c.authority}</div>
            <div className="text-xs text-muted-foreground truncate">
              {c.document_type || "Notice"}{c.license_plate ? ` · ${c.license_plate}` : ""}{c.due_date ? ` · due ${formatDate(c.due_date)}` : ""}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-sm font-semibold tabular-nums">{formatCurrency(c.amount)}</span>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        </button>
      ))}
    </CardContent>
  </Card>
);

export const ViewDocumentsCta = ({ onView }) => (
  <Card>
    <CardContent className="flex flex-col sm:flex-row items-center justify-between gap-3 py-5">
      <div>
        <div className="font-heading font-semibold">Review all extracted bills</div>
        <div className="text-sm text-muted-foreground">Search, filter and export your toll-bill data.</div>
      </div>
      <Button variant="outline" className="gap-2" data-testid="go-to-documents" onClick={onView}>
        View documents <ArrowRight className="h-4 w-4" />
      </Button>
    </CardContent>
  </Card>
);
